import { create } from 'zustand'

/**
 * Global save-status tracker. Components register a save when an
 * auto-save kicks off, and unregister when the network round-trip resolves.
 *
 * Uses a counter Map (not a Set) so overlapping saves for the same field
 * don't break the count: the user can type fast, trigger save 1, keep
 * typing, trigger save 2 — each begin increments and each end decrements.
 * The indicator only goes green when ALL in-flight saves finish.
 *
 * The header indicator subscribes to this store to show:
 *  - "Saving..." while any save is pending
 *  - "All changes saved" when idle and at least one save has completed
 *  - "Save failed" if the most recent save errored
 */
interface SaveStatusState {
  pending: Map<string, number>
  lastSavedAt: number | null
  errorCount: number
  begin: (key: string) => void
  end: (key: string, opts?: { error?: boolean }) => void
}

export const useSaveStatusStore = create<SaveStatusState>((set) => ({
  pending: new Map<string, number>(),
  lastSavedAt: null,
  errorCount: 0,
  begin: (key) =>
    set((s) => {
      const next = new Map(s.pending)
      next.set(key, (next.get(key) ?? 0) + 1)
      return { pending: next }
    }),
  end: (key, opts) =>
    set((s) => {
      const next = new Map(s.pending)
      const current = next.get(key) ?? 0
      if (current <= 1) next.delete(key)
      else next.set(key, current - 1)
      return {
        pending: next,
        lastSavedAt: opts?.error ? s.lastSavedAt : Date.now(),
        errorCount: opts?.error ? s.errorCount + 1 : 0,
      }
    }),
}))

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

export function selectSaveStatus(s: SaveStatusState): SaveStatus {
  if (s.pending.size > 0) return 'saving'
  if (s.errorCount > 0) return 'error'
  if (s.lastSavedAt) return 'saved'
  return 'idle'
}

/** Total in-flight save count, summed across all keys. */
export function selectPendingCount(s: SaveStatusState): number {
  let total = 0
  for (const n of s.pending.values()) total += n
  return total
}
